import {priority} from '@priority-software/priority-proto-api/lib';
import {useEffect, useState} from 'react';
import {useQuery} from 'react-query';
import {getFormColums} from '../../services/service';
import {serializeFields} from './utils';
import {BasicFormData} from '../../types';

export const useFieldSelect = (
    initSelectedNodes: Map<string, priority.netitems.nrest.ITreeNode>,
    fetchedFields: priority.netitems.nrest.ITreeNode[],
    treeNode: priority.netitems.nrest.ITreeNode,
    session
) => {
    const [selectedNodes, setSelectedNodes] =
        useState<Map<string, priority.netitems.nrest.ITreeNode>>(initSelectedNodes);

    const [zoom, setZoom] = useState<BasicFormData[]>([
        {id: treeNode.NodeId, title: treeNode.Title}
    ]);
    const [fields, setFields] = useState<priority.netitems.nrest.ITreeNode[]>();
    const [isDirty, setIsDirty] = useState(false);

    const updateFields = (fields: priority.netitems.nrest.ITreeNode[]) => {
        const sfields = serializeFields(fields);
        setFields(sfields);
    };

    const currentZoom = zoom[zoom.length - 1];
    const {isFetching: isLoadingFields, data: zoomFields} = useQuery(
        ['zoom', currentZoom, session],
        () => getFormColums(currentZoom.id),
        {
            enabled: !!currentZoom.id,
            staleTime: Infinity
        }
    );

    useEffect(() => {
        if (zoom.length === 1) {
            updateFields(fetchedFields);
        } else if (zoomFields) {
            updateFields(zoomFields);
        }
    }, [zoom, zoomFields, fetchedFields]);

    const handleZoom = (id: string, title: string) => {
        setZoom(prev => [...prev, {id, title}]);
    };

    const handleBackZoom = (index: number) => {
        setZoom(prev => [...prev].splice(0, index + 1));
    };

    const handleSelectField = (
        col: priority.netitems.nrest.ITreeNode,
        currentForm: BasicFormData
    ) => {
        setSelectedNodes(prev => {
            const updated = new Map(prev);
            updated.has(col.NodeId)
                ? updated.delete(col.NodeId)
                : updated.set(col.NodeId, {...col, FormTitle: currentForm.title});
            return updated;
        });
        setIsDirty(true);
    };

    const updatedSelectedNodes = (columns: priority.netitems.nrest.IReportCol[]) => {
        if (columns?.length !== selectedNodes.size) {
            for (const nodeId of selectedNodes.keys()) {
                if (!columns.find(c => c.NodeId === nodeId)) {
                    setSelectedNodes(prev => {
                        const updated = new Map(prev);
                        updated.delete(nodeId);
                        return updated;
                    });
                }
            }
        }
    };

    return {
        fields,
        selectedNodes,
        zoom,
        isLoading: isLoadingFields,
        isDirty,
        updatedSelectedNodes,
        handleSelectField,
        handleZoom,
        handleBackZoom
    };
};

export interface FieldActions {
    fields: priority.netitems.nrest.ITreeNode[];
    zoom: BasicFormData[];
    selectedNodes: Map<string, priority.netitems.nrest.ITreeNode>;
    isLoading: boolean;
    isDirty: boolean;
    updatedSelectedNodes: (columns: priority.netitems.nrest.IReportCol[]) => void;
    handleSelectField: (
        col: priority.netitems.nrest.ITreeNode,
        cureentForm?: BasicFormData
    ) => void;
    handleZoom: (Nodeid: string, title: string) => void;
    handleBackZoom: (index: number) => void;
}
